<template>
  <div class="SeatMapLegend">
    <Accordion
      v-if="seatClassItems.length"
      class="SeatMapLegend-seatTypes"
      :items="seatClassItems"
      type="multiple"
    >
      <template #header="{ item }">
        <div class="SeatMapLegend-seatTypeHeader">
          <SeatCell
            state="available"
            :variant="item.value as SeatClass"
            size="sm"
            aria-hidden="true"
            tabindex="-1"
          />
          <h3 class="SeatMapLegend-seatTypeTitle">
            {{ item.title }}
          </h3>
        </div>
      </template>
    </Accordion>

    <div class="SeatMapLegend-mapLegend">
      <h3 class="SeatMapLegend-mapLegendHeading">
        {{ t('ui.components.seatMap.legend.mapLegendHeading') }}
      </h3>

      <ul class="SeatMapLegend-units" role="list">
        <li class="SeatMapLegend-unit">
          <SeatCell
            state="unavailable"
            size="sm"
            aria-hidden="true"
            tabindex="-1"
          />
          <span>{{ t('ui.components.seatMap.legend.unavailableSeat') }}</span>
        </li>
        <li class="SeatMapLegend-unit">
          <SeatCell
            state="selected"
            size="sm"
            aria-hidden="true"
            tabindex="-1"
          />
          <span>{{ t('ui.components.seatMap.legend.selectedSeat') }}</span>
        </li>
        <li v-for="type of usedUnitTypes" :key="type" class="SeatMapLegend-unit">
          <Icon v-if="LEGEND_ICONS[type]" :icon="LEGEND_ICONS[type]!" size="sm" />
          <div v-else class="SeatMapUnit SeatMapUnit--table SeatMapLegend-tableSwatch" aria-hidden="true" />

          <span>{{ t(`ui.components.seatMap.units.${type}`) }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import Accordion from '../../molecules/Accordion/Accordion.vue';
import type { AccordionItem } from '../../molecules/Accordion/Accordion.model';
import SeatCell from '../../atoms/SeatCell/SeatCell.vue';
import Icon from '../../atoms/Icon/Icon.vue';
import type { IconName } from '../../atoms/Icon/Icon.model';
import { type SeatClass, type SeatMapSchema, type SeatMapUnitType } from './SeatMap.model';


const props = defineProps<{
  schema: SeatMapSchema;
}>();

const { t } = useI18n();

type LegendUnitType = Exclude<SeatMapUnitType, 'seat' | 'aisle' | 'info'>;

const LEGEND_UNIT_TYPES: LegendUnitType[] = ['wc', 'galley', 'exit', 'luggage', 'table', 'stairs'];
const LEGEND_ICONS: Partial<Record<LegendUnitType, IconName>> = {
  wc: 'human-male-female',
  galley: 'coffee',
  luggage: 'bag-suitcase',
  stairs: 'stepper',
  exit: 'arrow-right',
};

const allItems = computed(() => props.schema.layout.sections.flatMap((section) => section.rows.flatMap((row) => row.items)));

const usedUnitTypes = computed(() => {
  const present = new Set(allItems.value.map((item) => item.type));
  return LEGEND_UNIT_TYPES.filter((type) => present.has(type));
});

const usedSeatClasses = computed(() => {
  const present = new Set(allItems.value.filter((item) => item.type === 'seat').map((item) => item.seatClass));
  return (['premium', 'basic'] as const).filter((seatClass) => present.has(seatClass));
});

const seatClassItems = computed<AccordionItem[]>(() => usedSeatClasses.value.map((seatClass) => ({
  value: seatClass,
  title: t(`ui.components.seatMap.legend.seatClassSummary.${seatClass}`),
  content: t(`ui.components.seatMap.legend.seatClassDetails.${seatClass}`),
})));
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/text';

@layer cvo-ui {
  .SeatMapLegend {
    display: flex;
    flex-direction: column;
    gap: vars.$dimensions-d-4;

    &-seatTypeHeader {
      display: flex;
      align-items: center;
      gap: vars.$dimensions-d-2;
    }

    &-seatTypeTitle {
      @include text.text('md', vars.$typography-weight-strong);
    }

    &-mapLegendHeading {
      @include text.text('md', vars.$typography-weight-strong);
      margin-block-end: vars.$dimensions-d-3;
    }

    &-units {
      display: flex;
      flex-direction: column;
      gap: vars.$dimensions-d-3;
      list-style: none;
      padding: 0;
      margin: 0;
    }

    &-unit {
      display: flex;
      align-items: center;
      gap: vars.$dimensions-d-2;
      @include text.text('sm');
    }

    &-tableSwatch {
      width: 24px;
      height: 24px;
      min-height: 0;
    }
  }
}
</style>
